import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useAuth } from "context/AuthContext";
import { updateUserRole } from "layouts/dashboardLayout/pages/Users/services/graphqlService";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTokenContext, isAirportEntityType } from "utils/tokenUtils";

/**
 * Returns the primary label for an association:
 * "<Entity Name> · <Role>" (falling back gracefully when missing).
 */
export function getAssociationLabel(assn) {
  const role = assn.role || "user";
  const name = assn.entityName || assn.entityType || "";

  if (name && role) return `${name} · ${role}`;
  if (name) return name;
  if (role) return role;
  return "User";
}

/**
 * Returns true if this association is the currently active profile context.
 * context: { role, entityType, entityId } from token or profile.
 */
export function isActiveAssociation(assn, context) {
  if (!context) return false;
  const roleMatch = (assn.role || "") === (context.role || "");
  const entityTypeMatch =
    (assn.entityType || "") === (context.entityType || "");
  const entityIdMatch = (assn.entityId || "") === (context.entityId || "");
  return roleMatch && entityTypeMatch && entityIdMatch;
}

const showSwitchError = (err) => {
  const message = err?.message || "Failed to switch profile. Please try again.";
  window.dispatchEvent(
    new CustomEvent("globalNotification", {
      detail: {
        type: "error",
        title: "Switch profile failed",
        message,
      },
    }),
  );
};

/**
 * Renders the list of profile options for embedding inside a parent Menu (e.g. account dropdown).
 * currentContext: { role, entityType, entityId } from token (current context).
 */
function ProfileSwitchList({
  profile,
  associations,
  idToken,
  currentContext,
  onAfterSwitch,
  onError,
  onCloseParent,
}) {
  const [switchingIndex, setSwitchingIndex] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  // A ref (not state) so a fast double-click can't both slip through before
  // the re-render that disables the menu items actually happens.
  const isSwitchingRef = useRef(false);

  const previousAssn = associations.find((a) =>
    isActiveAssociation(a, currentContext),
  );

  const handleSelect = async (assn, index) => {
    if (!profile || !idToken) return;
    if (isSwitchingRef.current) return;
    if (isActiveAssociation(assn, currentContext)) {
      onCloseParent?.();
      return;
    }
    isSwitchingRef.current = true;
    setSwitchingIndex(index);
    setSwitchError(null);
    try {
      await updateUserRole(
        {
          profileId: profile.profileId,
          email: profile.email || "",
          name: profile.name || "User",
          role: assn.role || undefined,
          entityId: assn.entityId ?? undefined,
          entityType: assn.entityType ?? undefined,
        },
        idToken,
      );
      await onAfterSwitch?.(previousAssn);
      onCloseParent?.();
    } catch (err) {
      console.error("Failed to switch profile:", err);
      const message =
        err?.message || "Failed to switch profile. Please try again.";
      setSwitchError(message);
      onError?.(err);
    } finally {
      isSwitchingRef.current = false;
      setSwitchingIndex(null);
    }
  };

  return (
    <>
      {switchError && (
        <SoftBox px={2} py={1}>
          <SoftTypography variant="caption" color="error">
            {switchError}
          </SoftTypography>
        </SoftBox>
      )}
      {associations.map((assn, index) => {
        const isActive = isActiveAssociation(assn, currentContext);
        const isThisSwitching = switchingIndex === index;
        const rawType = assn.entityType || "";
        const entityType =
          rawType.toLowerCase() === "nonprofit"
            ? "Non-Profit"
            : rawType.toLowerCase() === "airport"
              ? "Airport"
              : rawType;
        return (
          <MenuItem
            key={index}
            selected={isActive}
            onClick={() => handleSelect(assn, index)}
            disabled={switchingIndex !== null}
            sx={{ py: 1.25 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {isActive ? (
                <Icon fontSize="small" color="primary">
                  check_circle
                </Icon>
              ) : (
                <Icon
                  fontSize="small"
                  sx={{ color: "text.secondary", opacity: 0.5 }}
                >
                  person_outline
                </Icon>
              )}
            </ListItemIcon>
            <ListItemText
              primary={getAssociationLabel(assn)}
              secondary={entityType || null}
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: isActive ? 600 : 400,
              }}
              secondaryTypographyProps={{
                variant: "caption",
                color: "text.secondary",
              }}
            />
            {isThisSwitching && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </MenuItem>
        );
      })}
    </>
  );
}

function ProfileSelector({ light = false, embedded = false, onCloseParent }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingIndex, setSwitchingIndex] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  const isSwitchingRef = useRef(false);

  const profile = auth.profile;
  const associations = auth.associations || [];
  const idToken = auth.user?.id_token;
  const currentContext = {
    role: auth.tokenRole,
    entityType: auth.tokenEntityType,
    entityId: auth.tokenEntityId,
  };

  const currentLabel =
    auth.tokenRole || auth.tokenEntityType
      ? getAssociationLabel({
          role: auth.tokenRole,
          entityType: auth.tokenEntityType,
          entityId: auth.tokenEntityId,
        })
      : "Profile";

  // After role switch: refresh token, refetch profile. If feature list is empty, switch back to previous role.
  // Airport context navigates to the airport pricing screen.
  const handleAfterSwitch = async (previousAssn) => {
    const refreshedUser = await auth.refreshAuth();
    if (!refreshedUser?.id_token) return;
    const fetchedProfile = await auth.fetchUserProfile(refreshedUser.id_token);
    const hasFeatureList =
      fetchedProfile?.feature &&
      Array.isArray(fetchedProfile.feature) &&
      fetchedProfile.feature.length > 0;
    if (!hasFeatureList && previousAssn) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "warning",
            title: "No menu for this role",
            message:
              "This role has no menu assigned. Switched back to your previous role.",
          },
        }),
      );
      await updateUserRole(
        {
          profileId: fetchedProfile?.profileId ?? profile?.profileId,
          email: fetchedProfile?.email ?? profile?.email ?? "",
          name: fetchedProfile?.name ?? profile?.name ?? "User",
          role: previousAssn.role ?? undefined,
          entityId: previousAssn.entityId ?? undefined,
          entityType: previousAssn.entityType ?? undefined,
        },
        refreshedUser.id_token,
      );
      // Brief delay so backend can persist the revert before we refetch
      await new Promise((r) => setTimeout(r, 500));
      const revertedUser = await auth.refreshAuth();
      if (revertedUser?.id_token) {
        let revertedProfile = await auth.fetchUserProfile(
          revertedUser.id_token,
        );
        const revertedHasMenu =
          revertedProfile?.feature &&
          Array.isArray(revertedProfile.feature) &&
          revertedProfile.feature.length > 0;
        if (!revertedHasMenu) {
          await new Promise((r) => setTimeout(r, 500));
          revertedProfile = await auth.fetchUserProfile(revertedUser.id_token);
        }
      }
      return;
    }

    const { entityType } = getTokenContext(refreshedUser.id_token);
    if (isAirportEntityType(entityType)) {
      navigate("/dashboard/pricing", { replace: true });
    }
  };

  // Embedded: render only the list for use inside parent Menu (e.g. account dropdown)
  if (embedded) {
    if (!profile || !idToken || associations.length <= 1) return null;
    return (
      <ProfileSwitchList
        profile={profile}
        associations={associations}
        idToken={idToken}
        currentContext={currentContext}
        onAfterSwitch={handleAfterSwitch}
        onError={showSwitchError}
        onCloseParent={onCloseParent}
      />
    );
  }

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const previousAssn = associations.find((a) =>
    isActiveAssociation(a, currentContext),
  );

  const handleSelect = async (assn, index) => {
    if (!profile || !idToken) return;
    if (isSwitchingRef.current) return;
    if (isActiveAssociation(assn, currentContext)) {
      handleClose();
      return;
    }

    isSwitchingRef.current = true;
    setSwitchingIndex(index);
    setSwitchError(null);
    try {
      await updateUserRole(
        {
          profileId: profile.profileId,
          email: profile.email || auth.user?.profile?.email || "",
          name: profile.name || auth.user?.profile?.name || "User",
          role: assn.role || undefined,
          entityId: assn.entityId ?? undefined,
          entityType: assn.entityType ?? undefined,
        },
        idToken,
      );
      await handleAfterSwitch(previousAssn);
      handleClose();
    } catch (err) {
      console.error("Failed to switch profile:", err);
      const message =
        err?.message || "Failed to switch profile. Please try again.";
      setSwitchError(message);
      showSwitchError(err);
    } finally {
      isSwitchingRef.current = false;
      setSwitchingIndex(null);
    }
  };

  // No selector when not authenticated or no profile
  if (!auth.isAuthenticated || !profile) {
    return null;
  }

  // Single association (or none): show current context only, no dropdown
  if (associations.length <= 1) {
    return (
      <SoftBox display="flex" alignItems="center" mr={1}>
        <SoftTypography
          variant="caption"
          color={light ? "white" : "text"}
          fontWeight="medium"
        >
          {currentLabel}
        </SoftTypography>
      </SoftBox>
    );
  }

  return (
    <>
      <SoftBox
        onClick={
          associations.length > 1 && switchingIndex === null
            ? (e) => handleOpen(e)
            : undefined
        }
        sx={{
          cursor:
            associations.length > 1 && switchingIndex === null
              ? "pointer"
              : "default",
          display: "flex",
          alignItems: "center",
          mr: 1,
        }}
      >
        <Icon
          fontSize="small"
          sx={{ color: light ? "white" : "inherit", mr: 0.5 }}
        >
          switch_account
        </Icon>
        <SoftTypography
          variant="button"
          fontWeight="medium"
          color={light ? "white" : "dark"}
          sx={{ textTransform: "none" }}
        >
          {currentLabel}
        </SoftTypography>
      </SoftBox>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setSwitchError(null);
          handleClose();
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 220, mt: 1.5 } }}
      >
        {switchError && (
          <SoftBox px={2} py={1}>
            <Alert
              severity="error"
              onClose={() => setSwitchError(null)}
              sx={{ py: 0.5 }}
            >
              <SoftTypography variant="caption">{switchError}</SoftTypography>
            </Alert>
          </SoftBox>
        )}
        {associations.map((assn, index) => {
          const isActive = isActiveAssociation(assn, currentContext);
          const isThisSwitching = switchingIndex === index;
          const rawType = assn.entityType || "";
          const entityType =
            rawType.toLowerCase() === "nonprofit"
              ? "Non-Profit"
              : rawType.toLowerCase() === "airport"
                ? "Airport"
                : rawType;

          return (
            <MenuItem
              key={index}
              selected={isActive}
              onClick={() => handleSelect(assn, index)}
              disabled={switchingIndex !== null}
            >
              <SoftBox display="flex" flexDirection="column" width="100%">
                <SoftTypography
                  variant="body2"
                  fontWeight={isActive ? "bold" : "regular"}
                  sx={{ textTransform: "none" }}
                >
                  {getAssociationLabel(assn)}
                </SoftTypography>
                {entityType && (
                  <SoftTypography
                    variant="caption"
                    color="text"
                    sx={{ color: "text.secondary" }}
                  >
                    {entityType}
                  </SoftTypography>
                )}
              </SoftBox>
              {isThisSwitching && (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default ProfileSelector;
